export type ReservationLifecycleStatus = 'upcoming' | 'active' | 'past'
export type ReservationLifecycleStatusOrUnknown =
  'cancelled' | ReservationLifecycleStatus | 'unknown'

interface ReservationLike {
  check_in?: string | Date | null
  check_out?: string | Date | null
  uplisting_status?: string | null
  status?: string | null
  external?: {
    uplisting?: {
      check_in?: string | Date | null
      check_out?: string | Date | null
      status?: string | null
      attributes?: {
        check_in?: string | Date | null
        check_out?: string | Date | null
        start_date?: string | Date | null
        end_date?: string | Date | null
      }
    }
  } | null
}

/**
 * Derive reservation lifecycle from stay dates (aligned with admin-api reservation-lifecycle).
 */
export function computeReservationLifecycleStatus(
  checkInRaw: string | Date | null | undefined,
  checkOutRaw: string | Date | null | undefined,
  now: Date = new Date()
): ReservationLifecycleStatus | null {
  if (!checkInRaw || !checkOutRaw) {
    return null
  }

  const checkIn = checkInRaw instanceof Date ? checkInRaw : new Date(checkInRaw)
  const checkOut = checkOutRaw instanceof Date ? checkOutRaw : new Date(checkOutRaw)

  const inMs = checkIn.getTime()
  const outMs = checkOut.getTime()
  if (Number.isNaN(inMs) || Number.isNaN(outMs)) {
    return null
  }

  const t = now.getTime()

  if (t < inMs) {
    return 'upcoming'
  }
  if (t >= inMs && t <= outMs) {
    return 'active'
  }
  return 'past'
}

export function getReservationStayDates(reservation: ReservationLike | null | undefined): {
  checkIn: string | null
  checkOut: string | null
} {
  if (!reservation || typeof reservation !== 'object') {
    return { checkIn: null, checkOut: null }
  }

  const uplisting = reservation.external?.uplisting
  const attrs = uplisting?.attributes || {}

  const checkIn =
    reservation.check_in ?? uplisting?.check_in ?? attrs.check_in ?? attrs.start_date ?? null
  const checkOut =
    reservation.check_out ?? uplisting?.check_out ?? attrs.check_out ?? attrs.end_date ?? null

  return {
    checkIn: checkIn != null ? String(checkIn) : null,
    checkOut: checkOut != null ? String(checkOut) : null,
  }
}

export function isReservationCancelled(reservation: ReservationLike | null | undefined): boolean {
  if (!reservation || typeof reservation !== 'object') {
    return false
  }

  // Check every status source independently rather than falling back through
  // `??` — a non-null but non-cancelled nested status must not mask a
  // cancelled top-level (or vice versa).
  const statuses = [
    reservation.uplisting_status,
    reservation.external?.uplisting?.status,
    reservation.status,
  ]

  return statuses.some(
    status => typeof status === 'string' && status.toLowerCase().includes('cancel')
  )
}

/**
 * Lifecycle for filtering and highlighting: cancellation status wins outright,
 * otherwise prefer dates, then stored status.
 */
export function getReservationLifecycleStatus(
  reservation: ReservationLike | null | undefined,
  now: Date = new Date()
): ReservationLifecycleStatusOrUnknown {
  if (isReservationCancelled(reservation)) {
    return 'cancelled'
  }

  const { checkIn, checkOut } = getReservationStayDates(reservation)
  const lifecycle = computeReservationLifecycleStatus(checkIn, checkOut, now)
  if (lifecycle) {
    return lifecycle
  }

  const s = (reservation as { status?: string } | null | undefined)?.status
  if (s === 'upcoming' || s === 'active' || s === 'past') {
    return s
  }
  return 'unknown'
}
